import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaBars, FaRobot, FaUser, FaSignOutAlt, FaChevronDown, FaUserCircle,
  FaSuitcase, FaGift, FaWallet, FaTimes, FaInstagram, FaFacebookF,
  FaLinkedin, FaGlobe, FaChevronRight
} from 'react-icons/fa';
import { useAdmin } from '../../../contexts/AdminContext';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import LoginModal from '../Auth/LoginModal';

const CURRENCIES = [
  { code: 'INR', label: 'INR - Indian Rupee', symbol: '₹' },
  { code: 'USD', label: 'USD - US Dollar', symbol: '$' },
  { code: 'EUR', label: 'EUR - Euro', symbol: '€' },
  { code: 'GBP', label: 'GBP - British Pound', symbol: '£' },
  { code: 'AED', label: 'AED - UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', label: 'SAR - Saudi Riyal', symbol: '﷼' }
];

const XIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const SimpleHeader = () => {
  const { companySettings } = useAdmin();
  const { settings, footer, showSearch, showUserMenu, getImageUrl, loading } = useWebsiteSettings();
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginTab, setLoginTab] = useState('login');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [currency, setCurrency] = useState(() => localStorage.getItem('selectedCurrency') || 'INR');

  const mobileUserRef = useRef(null);
  const desktopUserRef = useRef(null);
  const currencyRef = useRef(null);

  useEffect(() => {
    const closeMenus = (e) => {
      if (![mobileUserRef, desktopUserRef].some(r => r.current && r.current.contains(e.target))) setIsUserMenuOpen(false);
      if (!currencyRef.current || !currencyRef.current.contains(e.target)) setIsCurrencyOpen(false);
    };
    document.addEventListener('mousedown', closeMenus);
    return () => document.removeEventListener('mousedown', closeMenus);
  }, []);

  const logo = settings?.header?.logo || settings?.header?.logoUrl || companySettings?.logo;
  const logoUrl = (logo && !loading) ? getImageUrl(logo) : '';
  const companyName = settings?.footer?.companyName || settings?.name || companySettings?.companyName || 'BookYatri';
  const social = footer?.socialMedia || {};
  const profileImage = user?.profileImage
    ? (user.profileImage.startsWith('http') ? user.profileImage : `https://api.bookyatri.com/${user.profileImage}`)
    : null;

  const openLogin = (tab = 'login') => {
    setLoginTab(tab);
    setIsDrawerOpen(false);
    setIsUserMenuOpen(false);
    setShowLoginModal(true);
  };

  const selectCurrency = (code) => {
    setCurrency(code);
    localStorage.setItem('selectedCurrency', code);
    setIsCurrencyOpen(false);
    setIsDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    setIsDrawerOpen(false);
    navigate('/');
  };

  const openAiSearch = () => navigate('/ai-search');

  const accountLinks = [
    { label: 'My Profile', icon: FaUserCircle, path: '/my-profile' },
    { label: 'My Trip', icon: FaSuitcase, path: '/my-trips' },
    { label: 'My Wallet', icon: FaWallet, path: '/wallet' },
    { label: 'Offers', icon: FaGift, path: '/offers' }
  ];

  const renderUserDropdown = () => (
    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
      <div className="px-4 py-2 border-b border-gray-100">
        <div className="text-sm font-semibold text-gray-900">{user?.firstName || user?.name || 'User'}</div>
        <div className="text-xs text-gray-500 truncate">{user?.email}</div>
      </div>
      {accountLinks.map((l) => {
        const Icon = l.icon;
        return (
          <Link key={l.label} to={l.path}
            onClick={() => { setIsUserMenuOpen(false); setIsDrawerOpen(false); }}
            className="flex items-center space-x-3 px-4 py-2 text-gray-700 hover:bg-gray-100">
            <Icon className="text-sm text-gray-500" /><span className="text-sm">{l.label}</span>
          </Link>
        );
      })}
      <div className="border-t border-gray-100 my-1"></div>
      <button onClick={handleLogout} className="w-full flex items-center space-x-3 px-4 py-2 text-red-600 hover:bg-red-50">
        <FaSignOutAlt className="text-sm" /><span className="text-sm">Log Out</span>
      </button>
    </div>
  );

  const renderCurrencyMenu = () => (
    <div className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
      <div className="px-4 pt-1.5 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Select Currency</div>
      {CURRENCIES.map(c => (
        <button key={c.code} onClick={() => selectCurrency(c.code)}
          className={`w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-100 ${currency === c.code ? 'text-primary-600 font-semibold' : 'text-gray-700'}`}>
          <span>{c.label}</span>
          <span className="text-xs">{currency === c.code ? '✓' : c.symbol}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <header className="bg-white shadow-sm">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between px-3 py-2">
          <div className="flex items-center space-x-2">
            <button onClick={() => setIsDrawerOpen(true)} aria-label="Open menu" className="p-1.5 -ml-1 text-gray-700 hover:text-primary-600">
              <FaBars className="text-xl" />
            </button>
            <Link to="/" className="flex items-center">
              {logoUrl ? (
                <img src={logoUrl} alt={`${companyName} Logo`} className="h-8 object-contain" />
              ) : (
                <h1 className="text-lg font-bold text-primary-600">{companyName}</h1>
              )}
            </Link>
          </div>

          <div className="flex items-center">
            {showSearch && (
              <button onClick={openAiSearch} aria-label="AI Search" title="Ask AI to find hotels, flights, buses & more" className="p-1.5 text-gray-700 hover:text-primary-600">
                <FaRobot className="text-lg" />
              </button>
            )}

            {showUserMenu && (
              isAuthenticated ? (
                <div className="relative ml-1" ref={mobileUserRef}>
                  <button onClick={() => setIsUserMenuOpen(o => !o)} aria-label="Account" className="flex items-center gap-1.5 p-1.5 text-gray-700 hover:text-primary-600">
                    {profileImage ? (
                      <img src={profileImage} alt={user?.firstName || 'User'} className="h-7 w-7 rounded-full object-cover border border-gray-200" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                        {user?.firstName?.[0] || user?.email?.[0] || 'U'}
                      </div>
                    )}
                    <span className="max-w-[72px] truncate text-[12.5px] font-semibold text-gray-800">{user?.firstName || 'User'}</span>
                  </button>
                  {isUserMenuOpen && renderUserDropdown()}
                </div>
              ) : (
                <button onClick={() => openLogin('login')} aria-label="Login" className="p-1.5 ml-1 text-gray-700 hover:text-primary-600">
                  <FaUser className="text-lg" />
                </button>
              )
            )}
          </div>
        </div>

        {/* Desktop header */}
        <div className="hidden md:flex container mx-auto px-4 py-3 items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            {logoUrl ? (
              <img src={logoUrl} alt={`${companyName} Logo`} className="h-10 object-contain" onError={(e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
              }} />
            ) : null}
            <div style={{ display: logoUrl ? 'none' : 'block' }}>
              <h1 className="text-2xl font-bold text-primary-600">{companyName}</h1>
              <p className="text-xs text-gray-600">Your Travel Partner</p>
            </div>
          </Link>

          <div className="flex items-center space-x-1">
            {showSearch && (
              <div className="relative" ref={currencyRef}>
                <button onClick={() => { setIsCurrencyOpen(o => !o); setIsUserMenuOpen(false); }}
                  className="flex items-center space-x-1.5 p-2 text-gray-700 hover:text-primary-600 transition-colors" aria-label="Select currency">
                  <FaGlobe className="text-lg" />
                  <span className="hidden lg:block text-sm font-semibold">{currency}</span>
                  <FaChevronDown className="text-xs text-gray-400" />
                </button>
                {isCurrencyOpen && renderCurrencyMenu()}
              </div>
            )}

            {showSearch && (
              <button onClick={openAiSearch} aria-label="AI Search" title="Ask AI to find hotels, flights, buses & more" className="p-2 text-gray-700 hover:text-primary-600 transition-colors">
                <FaRobot className="text-lg" />
              </button>
            )}

            {showUserMenu && (
              isAuthenticated ? (
                <div className="relative" ref={desktopUserRef}>
                  <button onClick={() => setIsUserMenuOpen(o => !o)}
                    className="flex items-center space-x-2 p-2 text-gray-700 hover:text-primary-600 transition-colors">
                    {profileImage ? (
                      <img src={profileImage} alt={user?.firstName || 'User'} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                        {user?.firstName?.[0] || user?.email?.[0] || 'U'}
                      </div>
                    )}
                    <span className="text-sm font-medium">{user?.firstName || 'User'}</span>
                    <FaChevronDown className="text-xs text-gray-400" />
                  </button>
                  {isUserMenuOpen && renderUserDropdown()}
                </div>
              ) : (
                <button onClick={() => openLogin('login')}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-primary-600 border border-gray-300 rounded-lg hover:border-primary-400 transition-colors">
                  <FaUser className="text-sm" />
                  <span className="text-sm font-medium">Login</span>
                </button>
              )
            )}
          </div>
        </div>
      </header>

      {/* Mobile left drawer */}
      <div className={`fixed inset-0 z-[60] ${isDrawerOpen ? '' : 'pointer-events-none'}`} aria-hidden={!isDrawerOpen}>
        <div className={`absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ${isDrawerOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setIsDrawerOpen(false)} />
        <div className={`absolute left-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-xl flex flex-col overflow-hidden transition-transform duration-300 ease-in-out ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
            {logoUrl ? (
              <img src={logoUrl} alt={`${companyName} Logo`} className="h-8 object-contain" />
            ) : (
              <h2 className="text-lg font-bold text-primary-600">{companyName}</h2>
            )}
            <button onClick={() => setIsDrawerOpen(false)} aria-label="Close menu" className="p-1.5 text-gray-500 hover:text-gray-700">
              <FaTimes className="text-lg" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                {profileImage ? (
                  <img src={profileImage} alt={user?.firstName || 'User'} className="w-10 h-10 rounded-full object-cover border border-gray-200 shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold shrink-0">
                    {user?.firstName?.[0] || user?.email?.[0] || 'U'}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">{user?.firstName || 'User'}</div>
                  <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => openLogin('login')} className="py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700">
                  Login
                </button>
                <button onClick={() => openLogin('register')} className="py-2.5 border border-blue-600 text-blue-600 text-sm font-semibold rounded-lg hover:bg-blue-50">
                  Sign up
                </button>
              </div>
            )}

            <Link to="/my-trips" onClick={() => setIsDrawerOpen(false)}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50/50">
              <span className="flex items-center space-x-3">
                <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><FaSuitcase /></span>
                <span className="text-sm font-semibold text-gray-800">My Trip</span>
              </span>
              <FaChevronRight className="text-gray-400" />
            </Link>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <span className="flex items-center space-x-3 mb-3">
                <span className="w-9 h-9 rounded-lg bg-green-50 text-green-600 flex items-center justify-center"><FaGlobe /></span>
                <span className="text-sm font-semibold text-gray-800">Currency</span>
              </span>
              <div className="relative">
                <select value={currency} onChange={e => selectCurrency(e.target.value)}
                  className="w-full pl-3 pr-8 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-gray-50">
                  {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <Link to="/offers" onClick={() => setIsDrawerOpen(false)}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50/50">
              <span className="flex items-center space-x-3">
                <span className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center"><FaGift /></span>
                <span className="text-sm font-semibold text-gray-800">Offers</span>
              </span>
              <FaChevronRight className="text-gray-400" />
            </Link>
          </div>

          <div className="p-4 border-t border-gray-200 shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-gray-800 mb-3">Follow us</p>
              <div className="flex flex-wrap gap-3">
                <a href={social.instagram || '#'} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                  className="w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-pink-600 hover:bg-pink-50">
                  <FaInstagram />
                </a>
                <a href={social.facebook || '#'} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                  className="w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-blue-600 hover:bg-blue-50">
                  <FaFacebookF />
                </a>
                <a href={social.twitter || '#'} target="_blank" rel="noopener noreferrer" aria-label="X"
                  className="w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-900 hover:bg-gray-100">
                  <XIcon />
                </a>
                <a href={social.linkedin || '#'} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                  className="w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-blue-700 hover:bg-blue-50">
                  <FaLinkedin />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LoginModal isOpen={showLoginModal} initialTab={loginTab} onClose={() => setShowLoginModal(false)} />
    </>
  );
};

export default SimpleHeader;
